/*
 * App Router version of `../pushOrderHandler`.
 *
 * A thin shell around `storefrontApi.orders.provisionOrder`, which is where
 * all the substance lives and where it is tested (see the `provisionOrder`
 * suite in the `api` package). This file owns only the HTTP edges: the query
 * param, the head-start delay, and turning a provisioning failure into a 400
 * the payment provider will retry against.
 */
import { NextResponse, type NextRequest } from 'next/server'
import type { Option } from 'ecosystem'
import storefrontApi from 'api/storefront-api'
import { type OrderProvisionResult } from 'api/storefront-api/ordersEndpoint'
import { setRequestLocale } from '../../../common/api/setRequestLocale'
import { routeHandlerWithLogging } from '../../../logging/routeHandlerWithLogging'
import { type PushOrderHandlerOpts } from './types'

/**
 * Small head-start so the confirmation page usually provisions first;
 * correctness no longer relies on this delay — provisionOrder is idempotent
 * (skips if the order is already provisioned). Keep it short: Nexi requires a
 * 200 within 10s total, so delay + provisioning must stay well under that
 * budget or Nexi will retry and trigger another provision attempt.
 */
const HEAD_START_MS = 2000

export const pushOrderRouteHandler = (opts: PushOrderHandlerOpts) =>
  routeHandlerWithLogging(async function POST(request: NextRequest) {
    setRequestLocale(request)

    const orderId = request.nextUrl.searchParams.get('order_id')

    if (!orderId) {
      return NextResponse.json({ error: 'Missing query params.' }, { status: 400 })
    }

    await new Promise((resolve) => {
      setTimeout(resolve, HEAD_START_MS)
    })

    let provisionResult: Option<OrderProvisionResult> = null
    let provisionError

    try {
      provisionResult = await storefrontApi.orders.provisionOrder(
        orderId,
        opts.paymentProviderConfig,
        opts.shippingProviderConfig,
        // The push is a fallback: it provisions only orders the confirmation page never handled
        // (still CREATED and older than the freshness window). Fresh orders throw here → 400 → the
        // provider retries later. This keeps the confirmation page the single provisioner during
        // the post-checkout window, so the two paths never provision the same order concurrently.
        { asFallback: true }
      )
    } catch (e) {
      provisionError = e instanceof Error ? e.toString() : 'unknown'
    }

    if (provisionError || !provisionResult) {
      return NextResponse.json(
        { error: provisionError || 'unknown error while provisioning the order' },
        { status: 400 }
      )
    }

    return NextResponse.json({ message: 'Success' })
  })
